import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, EmptyRow, Spinner, TableWrap, Th } from '../../components/ui';
import { BASE, EvacuationArray, keys } from './api';
import type { SrBuilding } from './api';

/** Evacuation history for the building: headers and counts only, never names. */
export function EvacuationsTab({ building }: { building: SrBuilding }) {
  const q = useQuery({ queryKey: keys.evacuations(building.id), queryFn: () => api.get(`${BASE}/${building.id}/evacuations`, { schema: EvacuationArray }) });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const zone = building.timeZone;
  return (
    <TableWrap caption={t('srp.evac.table')}>
      <thead className="border-b border-slate-200 bg-slate-50">
        <tr>
          <Th>{t('srp.evac.col.started')}</Th>
          <Th>{t('srp.evac.col.kind')}</Th>
          <Th>{t('srp.evac.col.status')}</Th>
          <Th>{t('srp.evac.col.by')}</Th>
          <Th>{t('srp.evac.col.ended')}</Th>
          <Th>{t('srp.evac.col.companies')}</Th>
          <Th>{t('srp.evac.col.counts')}</Th>
        </tr>
      </thead>
      <tbody>
        {q.data.length === 0 ? <EmptyRow cols={7} /> : null}
        {q.data.map((e) => (
          <tr key={e.id} className="border-t border-slate-100">
            <td className="px-4 py-3">{formatDateTime(e.startedAt, zone)}</td>
            <td className="px-4 py-3">
              <Badge tone={e.kind === 'real' ? 'bad' : 'neutral'}>{t(`srp.evac.kind.${e.kind}`)}</Badge>
            </td>
            <td className="px-4 py-3">
              <Badge tone={e.status === 'active' ? 'warn' : 'good'}>{t(`srp.evac.status.${e.status}`)}</Badge>
            </td>
            <td className="px-4 py-3">
              {t(`srp.evac.source.${e.startedSource}`)}
              <div className="text-xs text-slate-700">{e.startedLabel}</div>
            </td>
            <td className="px-4 py-3">{formatDateTime(e.endedAt, zone)}</td>
            <td className="px-4 py-3">{e.companies}</td>
            <td className="px-4 py-3">
              {t('srp.evac.counts', e.totals)}
              <div className="text-xs text-slate-700">{e.namesDeleted ? t('srp.evac.namesDeleted') : t('srp.evac.namesKept')}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}
